import { computed, nextTick, ref, type Ref, watch } from "vue"
import {
  buildPanelEntry,
  columnConfigFromVisibleKeys,
  getColumnVisibilityKey,
  getColumnVisibilityLabel,
  hasSameColumnConfig,
  isToggleableColumn,
  normalizeReportingColumnConfig,
  type ColumnDefinitionWithVisibilityKey,
  type ColumnVisibilityPanelEntry,
  type KTableColumnDefinition,
  type KTableColumnVisibilityMode,
  type ReportingColumnConfig,
  type ToggleableColumn,
} from "@/components/KTable/columnVisibility"
import { getI18n } from "@/logic/i18n"
import { COLUMN_VISIBILITY, normalizeColumnVisibilityContext, shouldRefreshColumnVisibilityPreferences, useColumnVisibilityStore, type ColumnVisibilityContext, type TableColumnVisibilityState } from "@/store/columnVisibility"
import { useSnackbarsStore } from "@/store/snackbars"

export { getVisibleColumnKeysFromReportingConfig, normalizeReportingColumnConfig } from "@/components/KTable/columnVisibility"
export type { KTableColumnDefinition, KTableColumnVisibilityMode, ReportingColumnConfig } from "@/components/KTable/columnVisibility"

type UseKTableColumnVisibilityOptions = {
  tableName: Readonly<Ref<string | null>>
  columns: Readonly<Ref<KTableColumnDefinition[]>>
  mode: Readonly<Ref<KTableColumnVisibilityMode>>
  context: Readonly<Ref<ColumnVisibilityContext>>
  reportingColumnConfig: Readonly<Ref<ReportingColumnConfig | null>>
  hideColumnVisibility: Readonly<Ref<boolean>>
  clearRows: () => void
  reloadData: () => Promise<void>
  updateReportingColumnConfig: (columnConfig: ReportingColumnConfig) => void
}

export function useKTableColumnVisibility(options: UseKTableColumnVisibilityOptions) {
  const columnVisibilityStore = useColumnVisibilityStore()
  const isApplying = ref(false)
  let handledReportingColumnConfig: ReportingColumnConfig | null = null

  const currentTableState = computed(() => {
    return options.tableName.value ? columnVisibilityStore.getTableState(options.tableName.value, options.context.value) : null
  })

  const columnsWithVisibilityKeys = computed<ColumnDefinitionWithVisibilityKey[]>(() => {
    return options.columns.value.map((column) => ({
      ...column,
      resolvedVisibilityKey: getColumnVisibilityKey(column),
      resolvedVisibilityLabel: getColumnVisibilityLabel(column),
    }))
  })

  const uniqueToggleableColumns = computed<ToggleableColumn[]>(() => {
    const registeredColumnKeys = new Set<string>()

    return columnsWithVisibilityKeys.value.filter((column): column is ToggleableColumn => {
      if (!isToggleableColumn(column) || registeredColumnKeys.has(column.resolvedVisibilityKey)) {
        return false
      }

      registeredColumnKeys.add(column.resolvedVisibilityKey)

      return true
    })
  })

  const showColumnVisibilityUi = computed(() => {
    return !options.hideColumnVisibility.value && !!options.tableName.value && options.mode.value !== "disabled" && currentTableState.value?.hasLoaded === true && uniqueToggleableColumns.value.length > 0
  })

  const defaultColumns = computed<ReportingColumnConfig>(() => {
    return options.tableName.value ? columnVisibilityStore.getDefaultColumns(options.tableName.value, options.context.value) : {}
  })

  const resolvedColumns = computed<ReportingColumnConfig>(() => {
    const persistedColumns = options.tableName.value ? columnVisibilityStore.getResolvedColumns(options.tableName.value, options.context.value) : {}

    if (options.mode.value !== "reporting" || options.reportingColumnConfig.value === null) {
      return persistedColumns
    }

    return normalizeReportingColumnConfig(defaultColumns.value, options.reportingColumnConfig.value)
  })

  const columnVisibilityPanelColumns = computed<ColumnVisibilityPanelEntry[]>(() => {
    if (currentTableState.value?.hasLoaded !== true) {
      return []
    }

    return uniqueToggleableColumns.value.map((column) => buildPanelEntry(column, resolvedColumns.value, defaultColumns.value))
  })

  const hasChangesFromDefault = computed(() => {
    return uniqueToggleableColumns.value.some((column) => resolvedColumns.value[column.resolvedVisibilityKey] !== defaultColumns.value[column.resolvedVisibilityKey])
  })

  const visibleGridColumns = computed(() => {
    return columnsWithVisibilityKeys.value.filter((column) => !column.onlyForExport && isColumnVisible(column))
  })

  watch(
    () => ({
      tableName: options.tableName.value,
      mode: options.mode.value,
      context: normalizeColumnVisibilityContext(options.context.value),
      scopeKey: columnVisibilityStore.scopeKey,
    }),
    async ({ tableName, mode, context }) => {
      if (!tableName || mode === "disabled") {
        return
      }

      await loadPreferences(tableName, context)
    },
    { immediate: true },
  )

  watch(
    () => ({
      mode: options.mode.value,
      reportingColumnConfig: options.reportingColumnConfig.value ? { ...options.reportingColumnConfig.value } : null,
      isReady: currentTableState.value?.hasLoaded === true && currentTableState.value.isLoading === false,
      isApplying: isApplying.value,
      reportingScopeKey: getReportingScopeKey(),
    }),
    async ({ mode, reportingColumnConfig, isReady, isApplying: applying, reportingScopeKey }) => {
      if (mode !== "reporting" || !isReady || applying || reportingColumnConfig === null) {
        return
      }

      const normalizedColumnConfig = normalizeReportingColumnConfig(defaultColumns.value, reportingColumnConfig)
      if (hasSameColumnConfig(handledReportingColumnConfig, normalizedColumnConfig) && hasSameColumnConfig(reportingColumnConfig, normalizedColumnConfig)) {
        return
      }

      handledReportingColumnConfig = { ...normalizedColumnConfig }
      options.clearRows()

      if (!hasSameColumnConfig(reportingColumnConfig, normalizedColumnConfig)) {
        options.updateReportingColumnConfig({ ...normalizedColumnConfig })
        await nextTick()
      }

      if (getReportingScopeKey() !== reportingScopeKey || !hasSameColumnConfig(handledReportingColumnConfig, normalizedColumnConfig)) {
        return
      }

      await reloadRowsKeepingSelection()
    },
    { immediate: true },
  )

  function isColumnVisible(column: ColumnDefinitionWithVisibilityKey): boolean {
    if (column.hideFromColumnVisibility || !options.tableName.value || options.mode.value === "disabled" || options.hideColumnVisibility.value || !column.resolvedVisibilityKey) {
      return true
    }

    if (currentTableState.value?.hasLoaded !== true) {
      return false
    }

    if (column.alwaysVisible) {
      return true
    }

    return resolvedColumns.value[column.resolvedVisibilityKey] !== COLUMN_VISIBILITY.HIDDEN
  }

  function getVisibleColumnKeys(): string[] {
    if (currentTableState.value?.hasLoaded !== true) {
      return []
    }

    return uniqueToggleableColumns.value.filter((column) => resolvedColumns.value[column.resolvedVisibilityKey] !== COLUMN_VISIBILITY.HIDDEN).map((column) => column.resolvedVisibilityKey)
  }

  function getVisibleColumnsForExport(): KTableColumnDefinition[] {
    return columnsWithVisibilityKeys.value.filter((column) => (column.hide !== true || column.includeInDownloadWhenHidden === true) && column.hideInDownload !== true && !!column.resolvedVisibilityKey && isColumnVisible(column))
  }

  async function applyColumnVisibility(visibleColumnKeys: string[]): Promise<void> {
    const tableName = options.tableName.value
    if (!tableName || isApplying.value) {
      return
    }

    const context = normalizeColumnVisibilityContext(options.context.value)
    const tableState = columnVisibilityStore.ensureTableState(tableName, context)
    isApplying.value = true

    try {
      if (!(await loadPreferences(tableName, context))) {
        return
      }
      if (!isCurrentTableState(tableName, tableState)) {
        return
      }

      const nextColumns = columnConfigFromVisibleKeys(resolvedColumns.value, visibleColumnKeys, managedColumnKeys(resolvedColumns.value))
      if (hasSameColumnConfig(resolvedColumns.value, nextColumns)) {
        return
      }

      if (options.mode.value === "reporting") {
        await applyReportingColumns(tableName, tableState, nextColumns)
      } else {
        await applyUserColumns(tableName, context, tableState, nextColumns)
      }
    } finally {
      isApplying.value = false
    }
  }

  async function applyReportingColumns(tableName: string, tableState: TableColumnVisibilityState, nextColumns: ReportingColumnConfig): Promise<void> {
    handledReportingColumnConfig = { ...nextColumns }
    options.clearRows()
    options.updateReportingColumnConfig(nextColumns)
    await nextTick()

    if (!isCurrentTableState(tableName, tableState)) {
      return
    }

    await reloadRowsKeepingSelection()
  }

  async function applyUserColumns(tableName: string, context: ColumnVisibilityContext, tableState: TableColumnVisibilityState, nextColumns: ReportingColumnConfig): Promise<void> {
    let preferenceResponse

    try {
      preferenceResponse = await columnVisibilityStore.persistPreferences(tableName, nextColumns, context)
    } catch {
      useSnackbarsStore().error(getI18n().t("global.dataSaveFailure"))

      return
    }

    if (!isCurrentTableState(tableName, tableState)) {
      return
    }

    options.clearRows()
    columnVisibilityStore.applyPreferenceResponse(tableName, preferenceResponse, context)
    await nextTick()

    if (!isCurrentTableState(tableName, tableState)) {
      return
    }

    await reloadRowsKeepingSelection()
  }

  async function reloadRowsKeepingSelection(): Promise<void> {
    try {
      await options.reloadData()
    } catch {
      // Entry-list updater already reports load failure. Empty rows prevent stale payload/column combinations.
    }
  }

  async function loadPreferences(tableName: string, context: ColumnVisibilityContext): Promise<boolean> {
    try {
      await columnVisibilityStore.loadPreferences(tableName, context, shouldRefreshColumnVisibilityPreferences(tableName, context))

      return true
    } catch {
      // Store reports preference-load failure.
      return false
    }
  }

  function managedColumnKeys(columnConfig: ReportingColumnConfig): string[] {
    return uniqueToggleableColumns.value.map((column) => column.resolvedVisibilityKey).filter((columnKey) => Object.hasOwn(columnConfig, columnKey))
  }

  function isCurrentTableState(tableName: string, tableState: TableColumnVisibilityState): boolean {
    return options.tableName.value === tableName && currentTableState.value === tableState
  }

  function getReportingScopeKey(): string {
    return `${columnVisibilityStore.scopeKey}:table:${options.tableName.value ?? "none"}:context:${JSON.stringify(normalizeColumnVisibilityContext(options.context.value))}`
  }

  return {
    currentTableState,
    isApplying,
    showColumnVisibilityUi,
    columnVisibilityPanelColumns,
    hasChangesFromDefault,
    visibleGridColumns,
    getVisibleColumnsForExport,
    getVisibleColumnKeys,
    applyColumnVisibility,
  }
}
